import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './Textarea.styles';
import { TextareaProps } from './Textarea.types';
import { formatNumber, setSuffix } from '@/utils/helpers';
import { HelperText } from '@/components/UI/HelperText/HelperText';

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      appearance,
      label,
      withSymbolsCount,
      helperText,
      helperTextAlignment,
      hasError,
      height,
      hasScrollbar = true,
      maxSymbolsCount = 3000,
      symbolsCountTextPlacement = 'top',
      symbolsCountFontSize = 15,
      maxHeight,
      minHeight,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const { i18n, t } = useTranslation();

    function renderSymbolsCount() {
      const currentCount = value?.toString().length ?? 0;
      const symbolsLeft = maxSymbolsCount - currentCount;

      return (
        <S.TextareaSymbolsCount $fontSize={symbolsCountFontSize}>
          {t(setSuffix(i18n, symbolsLeft.toString(), 'common.n_symbols'), {
            n: formatNumber(symbolsLeft),
          })}
        </S.TextareaSymbolsCount>
      );
    }

    return (
      <div>
        {(label ||
          (withSymbolsCount && symbolsCountTextPlacement === 'top')) && (
          <S.TextareaHead>
            <S.TextareaLabel $variant="3">{label}</S.TextareaLabel>
            {withSymbolsCount &&
              symbolsCountTextPlacement === 'top' &&
              renderSymbolsCount()}
          </S.TextareaHead>
        )}

        <S.TextareaField
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
          $appearance={appearance}
          $height={height}
          $maxHeight={maxHeight}
          $minHeight={minHeight}
          $hasScrollbar={hasScrollbar}
          $hasError={hasError}
          maxLength={maxSymbolsCount}
        />

        {withSymbolsCount && symbolsCountTextPlacement === 'bottom' && (
          <S.TextareaFooter>
            {withSymbolsCount &&
              symbolsCountTextPlacement === 'bottom' &&
              renderSymbolsCount()}
          </S.TextareaFooter>
        )}
        {helperText && (
          <HelperText
            align={helperTextAlignment}
            helperText={helperText}
            hasError={hasError}
            hasBottomMargin={false}
          />
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
